
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { Loader2 } from "lucide-react";

const Microsoft365Callback = () => {
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Get the authorization code from the URL
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const error = urlParams.get('error');
        
        if (error) {
          throw new Error(`Microsoft returned an error: ${error}`);
        }
        
        if (!code) {
          throw new Error('No authorization code received from Microsoft');
        }

        // Get the account name if previously stored
        const accountName = sessionStorage.getItem('ms365_account_name') || '';
        sessionStorage.removeItem('ms365_account_name'); // Clean up

        // Exchange the code for tokens
        const { data, error: tokenError } = await supabase.functions.invoke('microsoft365-auth', {
          body: {
            action: 'exchangeCode',
            code,
            redirectUri: window.location.origin + '/auth/microsoft-callback',
            accountName
          }
        });

        if (tokenError) throw new Error(tokenError.message);
        if (!data?.success) throw new Error(data?.message || 'Failed to authenticate with Microsoft');

        setStatus("success");
        
        // Invalidate the email accounts query to refresh the list
        const queryClient = window.queryClient; // Access the query client from window (we'll set this up)
        if (queryClient) {
          queryClient.invalidateQueries({ queryKey: ['emailAccounts'] });
        }
        
        // Redirect back to settings after a brief delay
        setTimeout(() => {
          navigate("/settings");
        }, 1500);
      } catch (error: any) {
        console.error('Error handling Microsoft callback:', error);
        setStatus("error");
        setErrorMessage(error.message || 'An unknown error occurred');
        
        // Redirect back to settings after a longer delay
        setTimeout(() => {
          navigate("/settings");
        }, 3000);
      }
    };

    handleCallback();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full text-center">
        {status === "loading" && (
          <>
            <Loader2 className="animate-spin h-12 w-12 mx-auto text-primary mb-4" />
            <h2 className="text-2xl font-bold mb-2">Connecting Your Account</h2>
            <p className="text-gray-500">Please wait while we finish connecting your Microsoft 365 account...</p>
          </>
        )}
        
        {status === "success" && (
          <>
            <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold mb-2 text-green-600">Successfully Connected!</h2>
            <p className="text-gray-500">Your Microsoft 365 account has been successfully connected.</p>
          </>
        )}
        
        {status === "error" && (
          <>
            <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold mb-2 text-red-600">Connection Failed</h2>
            <p className="text-gray-500">{errorMessage}</p>
          </>
        )}
      </div>
    </div>
  );
};

export default Microsoft365Callback;
